import React from 'react';
import { Bell } from 'lucide-react';
import { greeting } from './tokens';

/**
 * Floating top bar shown over the map.
 * - Driver avatar (left)
 * - Greeting + name (center) — Online indicator + session timer when online
 * - Notification bell with unread dot (right)
 */
const TopGreetingBar = ({ user, isOnline, onlineForSeconds, unreadCount = 0, onBellClick }) => {
  const initials = ((user?.firstName || user?.first_name || 'D')[0] +
                    (user?.lastName || user?.last_name || '')[0]).toUpperCase();
  const name = user?.firstName || user?.first_name || 'Driver';

  const sessionLabel = (() => {
    if (!isOnline) return null;
    const s = Math.max(0, onlineForSeconds || 0);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m.toString().padStart(2, '0')}m online` : `${m}m online`;
  })();

  return (
    <div
      data-testid="driver-top-bar"
      className="fixed top-3 left-3 right-3 z-[40] bg-white/90 backdrop-blur-md rounded-2xl shadow-[0_6px_20px_rgba(0,0,0,0.10)] border border-gray-100"
      style={{ paddingTop: 'env(safe-area-inset-top, 0)' }}
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        {/* Avatar */}
        <div className="relative shrink-0">
          {user?.profile_picture || user?.face_photo_url ? (
            <img
              src={user.profile_picture || user.face_photo_url}
              alt={name}
              className="w-10 h-10 rounded-full object-cover ring-2 ring-white"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-semibold text-sm flex items-center justify-center ring-2 ring-white">
              {initials}
            </div>
          )}
          {isOnline && (
            <span
              className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 ring-2 ring-white animate-pulse"
              data-testid="driver-online-dot"
              aria-label="Online"
            />
          )}
        </div>

        {/* Greeting */}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 leading-tight">{greeting()},</p>
          <p className="text-[15px] font-semibold text-gray-900 truncate" data-testid="driver-greeting-name">
            {name}
          </p>
          {sessionLabel && (
            <p className="text-[11px] text-emerald-700 font-medium tabular-nums" data-testid="driver-session-timer">
              {sessionLabel}
            </p>
          )}
        </div>

        {/* Bell */}
        <button
          type="button"
          onClick={onBellClick}
          data-testid="driver-bell"
          aria-label="Notifications"
          className="relative w-11 h-11 rounded-full hover:bg-gray-100 active:bg-gray-200 flex items-center justify-center transition-colors"
        >
          <Bell className="w-5 h-5 text-gray-700" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default TopGreetingBar;
